import React, { useState, Fragment } from "react";
import { confirmSignIn } from "aws-amplify/auth";
import {
	isValidPassword,
	passwordsMatch,
	formatMessage,
} from "../../../js/formUtils";

/**
 * NewPassword Component - Handles AWS Amplify NEW_PASSWORD_REQUIRED challenge
 *
 * Props:
 * @param {string} email - User's email address for display
 * @param {object} user - The AWS Cognito user object from sign-in response
 * @param {function} onNewPasswordSuccess - Callback function called after successful password change
 * @param {function} onCancel - Callback function to return to login
 */

function NewPassword(props) {
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [error, setError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	/**
	 * Complete new password requirement using AWS Amplify
	 */
	const completeNewPassword = async () => {
		if (!isFormValid()) {
			setError("Please enter a valid password and make sure passwords match.");
			return;
		}

		setIsSubmitting(true);
		setError("");

		try {
			await confirmSignIn({
				challengeResponse: newPassword,
			});
			// Call success callback to continue login flow
			setIsSubmitting(false);
			if (props.onNewPasswordSuccess) {
				props.onNewPasswordSuccess(props.email, newPassword);
			}
		} catch (error) {
			console.error("Error setting new password:", error);
			let errorMessage = "Failed to set new password. Please try again.";

			if (error.message) {
				errorMessage = error.message;
			} else if (
				error.response &&
				error.response.data &&
				error.response.data.error
			) {
				errorMessage = formatMessage(error.response.data.error);
			}

			setError(errorMessage);
			setIsSubmitting(false);
		}
	};

	// Form validation
	const isFormValid = () => {
		return (
			isValidPassword(newPassword) &&
			passwordsMatch(newPassword, confirmPassword)
		);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		completeNewPassword();
	};

	return (
		<Fragment>
			<h3>Set New Password</h3>
			<p>
				{props.email ? (
					<Fragment>
						Your account <strong>{props.email}</strong> requires a new password.
						Please create a secure password below.
					</Fragment>
				) : (
					"Your account requires a new password. Please create a secure password below."
				)}
			</p>

			{error && <div className="error-message">{error}</div>}

			<form onSubmit={handleSubmit} className="login-form">
				<div className="form-group">
					<input
						type="password"
						id="new-password"
						value={newPassword}
						placeholder=" "
						onChange={(e) => setNewPassword(e.currentTarget.value)}
						required
						disabled={isSubmitting}
						autoComplete="new-password"
					/>
					<label htmlFor="new-password">New Password</label>
					{newPassword && !isValidPassword(newPassword) && (
						<div className="field-error">
							Password must be at least 8 characters
						</div>
					)}
				</div>

				<div className="form-group">
					<input
						type="password"
						id="confirm-password"
						value={confirmPassword}
						placeholder=" "
						onChange={(e) => setConfirmPassword(e.currentTarget.value)}
						required
						disabled={isSubmitting}
						autoComplete="new-password"
					/>
					<label htmlFor="confirm-password">Confirm New Password</label>
					{confirmPassword && newPassword !== confirmPassword && (
						<div className="field-error">Passwords do not match</div>
					)}
				</div>

				<div className="form-actions">
					<button
						type="submit"
						className="submit-button"
						disabled={!isFormValid() || isSubmitting}>
						{isSubmitting ? "Setting Password..." : "Set New Password"}
					</button>
				</div>
			</form>

			<div className="form-links">
				<a
					href="#"
					className="form-text-link"
					onClick={(e) => {
						e.preventDefault();
						props.onCancel();
					}}>
					Back to Login
				</a>
			</div>
		</Fragment>
	);
}

export default NewPassword;
